import { Injectable, signal } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { Router } from '@angular/router';
import { Observable, Subscription, tap, timer } from 'rxjs';
import { MarketplaceRefundRequest, MarketplaceRefundRequestStatus } from '../models/marketplace-refund-request.model';
import { PaginatedResult } from '../models/paginated-result.model';
import { ApiType, ServiceBase } from '../shared/classes/serviceBase';
import { environment } from '../../environments/environment';

/**
 * Seller refund requests on marketplace orders, backed by Retail.API /api/admin/refund-requests
 * (platform admin). A seller asks; approving refunds the buyer through the processor that took
 * the payment and takes the refunded amount back from the seller's marketplace earnings.
 *
 * Also owns the sidebar badge: the number of requests waiting, polled once a minute while an
 * admin is signed in.
 */
@Injectable({ providedIn: 'root' })
export class MarketplaceRefundRequestsService extends ServiceBase {
  private readonly endpoint = 'api/admin/refund-requests';
  static readonly PAGE_SIZE = 12;
  private static readonly POLL_MS = 60_000;

  /** Requests waiting for review, for the sidebar badge. */
  readonly pendingCount = signal(0);
  private polling?: Subscription;

  constructor(http: HttpClient, router: Router) {
    super(http, router);
  }

  protected override getBusinessIdValue(): string {
    return environment.marketplaceBusinessId;
  }

  protected override getTerminalIdValue(): string {
    return environment.marketplaceTerminalId;
  }

  private get headers() {
    return { businessId: this.getBusinessIdValue(), terminalId: this.getTerminalIdValue() };
  }

  getRequests(status: MarketplaceRefundRequestStatus | null, pageNumber: number): Observable<PaginatedResult<MarketplaceRefundRequest>> {
    let params = new HttpParams()
      .set('pageNumber', pageNumber)
      .set('pageSize', MarketplaceRefundRequestsService.PAGE_SIZE);
    if (status) params = params.set('status', status);
    return this.get<PaginatedResult<MarketplaceRefundRequest>>(`${this.endpoint}/GetRequests`, ApiType.RETAIL, params);
  }

  getRequest(id: string): Observable<MarketplaceRefundRequest> {
    return this.get<MarketplaceRefundRequest>(`${this.endpoint}/GetRequest/${encodeURIComponent(id)}`, ApiType.RETAIL);
  }

  approve(id: string, note?: string): Observable<MarketplaceRefundRequest> {
    return this.post<MarketplaceRefundRequest>(
      `${this.endpoint}/${encodeURIComponent(id)}/approve`, { reason: note ?? null }, ApiType.RETAIL, this.headers
    ).pipe(tap(() => this.refreshCount()));
  }

  reject(id: string, reason: string): Observable<MarketplaceRefundRequest> {
    return this.post<MarketplaceRefundRequest>(
      `${this.endpoint}/${encodeURIComponent(id)}/reject`, { reason }, ApiType.RETAIL, this.headers
    ).pipe(tap(() => this.refreshCount()));
  }

  /** Starts the badge poll (idempotent). */
  startPolling(): void {
    if (this.polling) return;
    this.polling = timer(0, MarketplaceRefundRequestsService.POLL_MS).subscribe(() => this.refreshCount());
  }

  stopPolling(): void {
    this.polling?.unsubscribe();
    this.polling = undefined;
    this.pendingCount.set(0);
  }

  refreshCount(): void {
    const params = new HttpParams().set('status', 'Pending');
    this.get<{ count: number }>(`${this.endpoint}/count`, ApiType.RETAIL, params).subscribe({
      next: r => this.pendingCount.set(r?.count ?? 0),
      // A badge is not worth an error on screen; the next poll tries again.
      error: () => {}
    });
  }
}
